import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import * as Icons from 'react-feather'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import FileUploadModal from '../../shared/file-upload/FileUploadModal'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'

const isImageFile = (mimeType = '', name = '') => {
  const lower = String(mimeType || '').toLowerCase()
  if (lower.startsWith('image/')) return true
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some((ext) => String(name || '').toLowerCase().endsWith(ext))
}

const buildPreviewUrl = (fileId) => `${getApiBaseUrl()}/file/download/${fileId}?inline=1`

function HandoverItemSection({ handoverRegisterId, itemType, emptyLabel }) {
  const [alert, setAlert] = useState(null)
  const [availableItems, setAvailableItems] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [fileMap, setFileMap] = useState({})
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadTarget, setUploadTarget] = useState(null)

  const normalizedType = String(itemType || '').toLowerCase()

  const selectedItemIds = useMemo(() => new Set(selectedItems.map((item) => item.HandoverItemId)), [selectedItems])

  const fetchAvailableItems = useCallback(async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + '/handoveritems/getAll')
      const payload = response?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const filtered = list.filter((item) => String(item?.HandoverItemType || '').toLowerCase() === normalizedType)
      setAvailableItems(filtered)
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to load handover items', 'Error', setAlert)
    }
  }, [normalizedType])

  const fetchSelectedItems = useCallback(async () => {
    if (!handoverRegisterId) return
    try {
      const response = await axios.get(getApiBaseUrl() + '/handoverregister/getitemsbyregisterid', {
        params: { HandoverRegisterId: handoverRegisterId }
      })
      const payload = response?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const filtered = list.filter((item) => String(item?.HandoverItemType || '').toLowerCase() === normalizedType)
      setSelectedItems(filtered)
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to load selected items', 'Error', setAlert)
    }
  }, [handoverRegisterId, normalizedType])

  const fetchFilesForItems = useCallback(async (items) => {
    if (!items?.length) {
      setFileMap({})
      return
    }
    try {
      const requests = items.map((item) =>
        axios.post(getApiBaseUrl() + '/file/list', {
          entityId: item.HandoverRegisterItemId,
          entityType: 'handoverregisteritem'
        })
          .then((response) => ({
            id: item.HandoverRegisterItemId,
            files: Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data?.data?.[0])
                ? response.data.data[0]
                : []
          }))
      )
      const results = await Promise.all(requests)
      const nextMap = {}
      results.forEach((row) => {
        nextMap[row.id] = row.files || []
      })
      setFileMap(nextMap)
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    fetchAvailableItems()
    fetchSelectedItems()
  }, [fetchAvailableItems, fetchSelectedItems])

  useEffect(() => {
    fetchFilesForItems(selectedItems)
  }, [fetchFilesForItems, selectedItems])

  const handleToggle = async (item) => {
    if (!handoverRegisterId) return
    const existing = selectedItems.find((row) => row.HandoverItemId === item.HandoverItemId)
    if (existing) {
      await handleDelete(existing)
      return
    }
    try {
      const response = await axios.post(getApiBaseUrl() + '/handoverregister/saveitem', {
        HandoverRegisterId: handoverRegisterId,
        HandoverItemId: item.HandoverItemId,
        UserId: getStorageValues('userId')
      })
      if (response.status === 200) {
        fetchSelectedItems()
      }
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to add item', 'Error', setAlert)
    }
  }

  const handleDelete = async (row) => {
    if (!row?.HandoverRegisterItemId) return
    try {
      const response = await axios.post(getApiBaseUrl() + '/handoverregister/deleteitem', {
        HandoverRegisterItemId: row.HandoverRegisterItemId,
        UserId: getStorageValues('userId')
      })
      if (response.status === 200) {
        fetchSelectedItems()
      }
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to delete item', 'Error', setAlert)
    }
  }

  const openUploadModal = (row) => {
    setUploadTarget(row)
    setUploadModalOpen(true)
  }

  const handleUploadConfirm = async ({ category, files, title }) => {
    if (!uploadTarget?.HandoverRegisterItemId) return
    try {
      const formData = new FormData()
      formData.append('entityId', uploadTarget.HandoverRegisterItemId)
      formData.append('entityType', 'handoverregisteritem')
      formData.append('fileCategory', category)
      formData.append('fileTitle', title || uploadTarget?.HandoverItemTitle || '')
      formData.append('UserId', getStorageValues('userId'))
      ;(files || []).forEach((file) => {
        formData.append('files', file, file.name)
      })
      await axios.post(getApiBaseUrl() + '/file/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      fetchSelectedItems()
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to upload file', 'Error', setAlert)
    }
  }

  const renderFiles = (row) => {
    const files = fileMap[row.HandoverRegisterItemId] || []
    if (!files.length) {
      return <span className="text-muted small">No files</span>
    }
    return (
      <div className="d-flex flex-column gap-1">
        {files.map((file) => {
          const previewUrl = buildPreviewUrl(file.FileLibraryId)
          const isImage = isImageFile(file.MimeType, file.OriginalName)
          return (
            <div key={file.FileLibraryId} className="d-flex align-items-center gap-2">
              {isImage ? (
                <a href={previewUrl} target="_blank" rel="noreferrer">
                  <img
                    src={previewUrl}
                    alt={file.OriginalName}
                    style={{ width: '48px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                </a>
              ) : (
                <a href={previewUrl} target="_blank" rel="noreferrer" className="small">
                  {file.OriginalName}
                </a>
              )}
              <a href={previewUrl} target="_blank" rel="noreferrer" className="small text-primary">
                Preview
              </a>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="border rounded p-3 mb-3">
        <div className="mb-3">
          {availableItems.length > 0 ? (
            availableItems.map((item) => {
              const key = item.HandoverItemId ?? item.HandoverItemTitle
              const isSelected = selectedItemIds.has(item.HandoverItemId)
              return (
                <span
                  key={key}
                  role="button"
                  className={`badge ${isSelected ? 'bg-outline-secondary' : 'bg-soft-secondary'} m-1`}
                  style={{ fontSize: '14px', userSelect: 'none' }}
                  onClick={() => handleToggle(item)}
                >
                  {item.HandoverItemTitle || ''}
                </span>
              )
            })
          ) : (
            <span className="text-muted small">{emptyLabel}</span>
          )}
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Title</th>
                <th style={{ width: '30%' }}>Description</th>
                <th style={{ width: '30%' }}>Image/Document</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted">No selected items</td>
                </tr>
              )}
              {selectedItems.map((row) => (
                <tr key={row.HandoverRegisterItemId || row.HandoverItemId}>
                  <td>{row.HandoverItemTitle || '-'}</td>
                  <td>{row.Description || '-'}</td>
                  <td>{renderFiles(row)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-2 btn-ho-sm"
                      onClick={() => openUploadModal(row)}
                    >
                      <Icons.Upload size={14} className="me-1" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger btn-ho-sm"
                      onClick={() => handleDelete(row)}
                    >
                      <Icons.Trash2 size={14} className="me-1" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FileUploadModal
        show={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        categories={[itemType, 'General', 'Other']}
        onConfirm={handleUploadConfirm}
      />
    </>
  )
}

export default HandoverItemSection
